<template>
  <div class="user-management">
    <h1 class="mb-4">{{ $t('user_management.title') }}</h1>
    
    <div class="row mb-4">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ $t('user_management.users_section') }}</h5>
            <button class="btn btn-primary" @click="showAddUserModal" v-if="isAdmin">
              {{ $t('user_management.add_user_btn') }}
            </button>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-3">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            
            <div v-else-if="users.length === 0" class="text-center py-3">
              <p>{{ $t('user_management.no_users') }}</p>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ $t('user_management.table.email') }}</th>
                    <th>{{ $t('user_management.table.name') }}</th>
                    <th>{{ $t('user_management.table.admin') }}</th>
                    <th>{{ $t('user_management.table.status') }}</th>
                    <th>{{ $t('user_management.table.created') }}</th>
                    <th>{{ $t('user_management.table.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td>{{ user.email }}</td>
                    <td>{{ user.first_name }} {{ user.last_name }}</td>
                    <td>
                      <span 
                        :class="user.is_admin ? 'badge bg-primary' : 'badge bg-secondary'"
                      >
                        {{ user.is_admin ? 'Admin' : 'User' }}
                      </span>
                    </td>
                    <td>
                      <span 
                        :class="user.is_active ? 'badge bg-success' : 'badge bg-danger'"
                      >
                        {{ user.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td>{{ formatDate(user.created_at) }}</td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-primary me-2" 
                        @click="editUser(user)"
                      >
                        {{ $t('user_management.table.edit') }}
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-danger" 
                        @click="toggleUserStatus(user)"
                        v-if="isAdmin"
                      >
                        {{ user.is_active ? $t('user_management.table.deactivate') : $t('user_management.table.activate') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Add User Modal -->
    <div class="modal fade" id="addUserModal" tabindex="-1" ref="addUserModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editMode ? $t('user_management.modal.edit_title') : $t('user_management.modal.add_title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="modalError" class="alert alert-danger">{{ modalError }}</div>
            
            <form @submit.prevent="saveUser">
              <div class="mb-3">
                <label for="email" class="form-label">{{ $t('user_management.table.email') }}</label>
                <input 
                  type="email" 
                  class="form-control" 
                  id="email" 
                  v-model="userForm.email" 
                  :disabled="editMode"
                  required
                >
              </div>
              
              <div class="mb-3">
                <label for="firstName" class="form-label">{{ $t('user_management.modal.first_name') }}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="firstName" 
                  v-model="userForm.firstName"
                >
              </div>
              
              <div class="mb-3">
                <label for="lastName" class="form-label">{{ $t('user_management.modal.last_name') }}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="lastName" 
                  v-model="userForm.lastName"
                >
              </div>
              
              <div class="mb-3">
                <label for="password" class="form-label">
                    {{ editMode ? $t('user_management.modal.password_hint') : $t('user_management.modal.password') }}
                </label>
                <input 
                  type="password" 
                  class="form-control" 
                  id="password" 
                  v-model="userForm.password"
                  :required="!editMode"
                >
              </div>
              
              <div class="form-check mb-3" v-if="isAdmin">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  id="isAdmin" 
                  v-model="userForm.isAdmin"
                >
                <label class="form-check-label" for="isAdmin">{{ $t('user_management.modal.is_admin') }}</label>
              </div>
              
              <div class="form-check mb-3" v-if="editMode && isAdmin">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  id="isActive" 
                  v-model="userForm.isActive"
                >
                <label class="form-check-label" for="isActive">{{ $t('user_management.modal.is_active') }}</label>
              </div>
              
              <div class="d-grid">
                <button 
                  type="submit" 
                  class="btn btn-primary" 
                  :disabled="modalLoading"
                >
                  <span v-if="modalLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ editMode ? $t('user_management.modal.submit_update') : $t('user_management.modal.submit_add') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as bootstrap from 'bootstrap'

export default {
  name: 'UserManagement',
  data() {
    return {
      loading: false,
      modalLoading: false,
      modalError: null,
      editMode: false,
      currentUserId: null,
      userForm: {
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        isAdmin: false,
        isActive: true
      },
      modal: null
    }
  },
  computed: {
    ...mapGetters('users', ['allUsers']),
    ...mapGetters('auth', ['isAdmin']),
    
    users() {
      return this.allUsers || []
    }
  },
  methods: {
    ...mapActions('users', ['fetchUsers', 'createUser', 'updateUser']),
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    
    async loadUsers() {
      this.loading = true
      try {
        await this.fetchUsers()
      } catch (error) {
        console.error('Failed to fetch users:', error)
      } finally {
        this.loading = false
      }
    },
    
    showAddUserModal() {
      this.editMode = false
      this.modalError = null
      this.currentUserId = null
      this.userForm = {
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        isAdmin: false,
        isActive: true
      }
      this.modal.show()
    },
    
    editUser(user) {
      this.editMode = true
      this.modalError = null
      this.currentUserId = user.id
      this.userForm = {
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        password: '',
        isAdmin: !!user.is_admin,
        isActive: !!user.is_active
      }
      this.modal.show()
    },
    
    async toggleUserStatus(user) {
      if (confirm(`Are you sure you want to ${user.is_active ? 'deactivate' : 'activate'} this user?`)) {
        try {
          await this.updateUser({
            userId: user.id,
            userData: {
              isActive: !user.is_active
            }
          })
          await this.loadUsers()
        } catch (error) {
          console.error('Failed to update user status:', error)
          alert('Failed to update user status')
        }
      }
    },
    
    async saveUser() {
      this.modalLoading = true
      this.modalError = null
      
      try {
        if (this.editMode) {
          // Update existing user
          const result = await this.updateUser({
            userId: this.currentUserId,
            userData: {
              firstName: this.userForm.firstName,
              lastName: this.userForm.lastName,
              isAdmin: this.userForm.isAdmin,
              isActive: this.userForm.isActive,
              password: this.userForm.password
            }
          })
          
          if (result.success) {
            this.modal.hide()
            await this.loadUsers()
          } else {
            this.modalError = result.error
          }
        } else {
          // Create new user
          const result = await this.createUser({
            email: this.userForm.email,
            password: this.userForm.password,
            firstName: this.userForm.firstName,
            lastName: this.userForm.lastName,
            isAdmin: this.userForm.isAdmin
          })
          
          if (result.success) {
            this.modal.hide()
            await this.loadUsers()
          } else {
            this.modalError = result.error
          }
        }
      } catch (error) {
        this.modalError = 'An unexpected error occurred'
        console.error(error)
      } finally {
        this.modalLoading = false
      }
    }
  },
  mounted() {
    this.loadUsers()
    this.modal = new bootstrap.Modal(this.$refs.addUserModal)
  }
}
</script>